import {
  useOrgIntegrations,
  type IntegrationEnhanced,
} from "@/api/integrations";
import { useRowReservation } from "@/components/rail-kit/RailReveal";
import { hasPlatformEntitySelector } from "@/constants/integration-platforms";
import { IntegrationIcon } from "@cohesive/ui-components";
import { Compass } from "lucide-react";
import { useState } from "react";
import {
  CANVAS_GLYPH_STROKE,
  CANVAS_MENU_GLYPH,
} from "../../canvasToolbarComposition";
import {
  AppGrid,
  RowList,
  Tray,
  TrayDeck,
  TrayEmpty,
  TrayFind,
  TrayFoot,
  TraySection,
} from "../CanvasTrayContent";

/**
 * The resting "Not connected" list is a short set worth connecting: the
 * connectors the canvas can actually use (those with an entity selector),
 * in the catalogue's own order — starred first, then by weight, the order
 * Settings › Connectors shows — capped to what fits the deck at rest. The
 * search spans the whole canvas-usable catalogue; the foot's "+" and
 * Browse all are the door to everything else.
 */
const AVAILABLE_AT_REST = 4;

const canvasUsable = (i: IntegrationEnhanced) =>
  i.status === "enabled" && hasPlatformEntitySelector(i.platform);

interface ConnectorsTrayProps {
  /** A connected source: open its entity picker. */
  onPickConnected: (integration: IntegrationEnhanced) => void;
  /** An unconnected source: its own settings detail, the connect flow (ROUND2 §5). */
  onConnect: (integration: IntegrationEnhanced) => void;
  /** Settings › Connectors — Browse all and the Add tile land here. */
  onManage: () => void;
}

/**
 * Connectors — the @ family's canvas door. A grid of the sources you have
 * (found by recognition) over a list of the ones you could have (read by
 * name), both filtered by the find-and-add foot that stays put when a
 * filter empties them. The same roster the old menu read, with its
 * loading, empty and error states.
 */
export function ConnectorsTray({
  onPickConnected,
  onConnect,
  onManage,
}: ConnectorsTrayProps) {
  const { data: integrations = [], isPending, isError } = useOrgIntegrations();
  const [query, setQuery] = useState("");
  const { reservedRows, skeletonVisible } = useRowReservation({
    active: true,
    pending: isPending,
    fallbackRows: 3,
  });

  const q = query.trim().toLowerCase();
  const match = (i: IntegrationEnhanced) => i.name.toLowerCase().includes(q);
  const connected = integrations
    .filter(
      (i) =>
        i.connected &&
        i.integration_connection_id &&
        hasPlatformEntitySelector(i.platform),
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter(match);
  const catalogue = integrations.filter((i) => !i.connected && canvasUsable(i));
  const available = q
    ? catalogue.filter(match)
    : catalogue.slice(0, AVAILABLE_AT_REST);

  const mark = (i: IntegrationEnhanced, size: "grid" | "row") => (
    <IntegrationIcon
      platform={i.platform}
      name={i.name}
      imageURL={i.metadata?.image_url}
      className={size === "grid" ? "size-6 rounded" : "size-4 rounded"}
    />
  );

  const connectedBand = isPending ? (
    <div
      role="status"
      aria-label="Loading connectors"
      className="flex flex-col gap-0.5"
    >
      {Array.from({ length: reservedRows }, (_, i) => (
        <div
          key={i}
          className={
            skeletonVisible ? "h-8 animate-pulse rounded-sm bg-n-05" : "h-8"
          }
        />
      ))}
    </div>
  ) : connected.length ? (
    <AppGrid
      items={connected.map((i) => ({
        key: i.id,
        name: i.name,
        lead: mark(i, "grid"),
      }))}
      onChange={(k) => {
        const i = connected.find((c) => c.id === k);
        if (i) onPickConnected(i);
      }}
    />
  ) : isError && !integrations.length ? (
    <TrayEmpty>Couldn&apos;t load connectors.</TrayEmpty>
  ) : q ? (
    <TrayEmpty>No connected source matches &ldquo;{query}&rdquo;.</TrayEmpty>
  ) : (
    <TrayEmpty>No connectors to mention yet.</TrayEmpty>
  );

  const rows = [
    ...available.map((i) => ({
      key: i.id,
      name: i.name,
      lead: mark(i, "row"),
      action: { label: "Connect", onSelect: () => onConnect(i) },
    })),
    ...(q
      ? []
      : [
          {
            key: "browse",
            name: "Browse all connectors",
            lead: (
              <Compass
                size={CANVAS_MENU_GLYPH}
                strokeWidth={CANVAS_GLYPH_STROKE}
                aria-hidden="true"
              />
            ),
          },
        ]),
  ];

  return (
    <Tray>
      <TrayDeck>
        <TraySection label="Connected" bare>
          {connectedBand}
        </TraySection>
        <TraySection label="Not connected" bare>
          {rows.length ? (
            <RowList
              items={rows}
              onChange={(k) => {
                const i = available.find((c) => c.id === k);
                if (i) onConnect(i);
                else onManage();
              }}
            />
          ) : (
            <TrayEmpty>
              Nothing left to connect under &ldquo;{query}&rdquo;.
            </TrayEmpty>
          )}
        </TraySection>
      </TrayDeck>
      <TrayFoot>
        <TrayFind
          value={query}
          onChange={setQuery}
          placeholder="Search connectors…"
          label="Search connectors"
          addLabel="Add connector"
          onAdd={onManage}
        />
      </TrayFoot>
    </Tray>
  );
}
